import { useTheme } from '@mui/material';

import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ContentTemplate,
  HeaderWithPill,
  HtmlTrans,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import {
  MedicalCondition,
  useGetEnabledMedicalConditions,
} from '../../../../api-refactored/candidate/useGetEnabledMedicalConditions';
import { usePutDisableMedicalConditions } from '../../../../api-refactored/candidate/usePutDisableMedicalConditions';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import {
  GET_DROPDOWN_MEDICAL_CONDITIONS_QUERY_KEY,
  GET_ENABLED_MEDICAL_CONDITIONS_QUERY_KEY,
} from '../../../../constants/queryKeys';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import AddMedicalConditionModal from './AddMedicalConditionModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useStyles } from './style';

const getTableColumns = (
  t: TFunction,
  classes: Record<string,string>,
  tableState: TableState,
  onDeleteDialogOpen: () => void,
  setSelectedMedicalCondition: React.Dispatch<React.SetStateAction<MedicalCondition>>,
  isEnableDelete: boolean
): MUIDataTableColumnDef[] => [
  {
    name: 'name',
    label: t('admin->>AccessArrangementConfiguration.MedicalCondition'),
    options: {
      sort: true,
      searchable: false,
      filter: false,
    },
  },
  {
    name: 'action',
    label: t('admin->>AccessArrangementConfiguration.Actions'),
    options: {
      sort: false,
      searchable: false,
      filter: false,
      customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
        DeleteButton({
          deleteHandler: () => {
            setSelectedMedicalCondition(tableState.data[dataIndex]);
            onDeleteDialogOpen();
          },
          label: t('component->>IconLabel.Remove'),
          className: classes.deleteButton,
          buttonId: `medical-condition-${rowIndex}`,
        }),
      display: `${isEnableDelete}`,
    },
  },
];

interface MedicalConditionConfigurationProps {
  isEnableCreate: boolean;
  isEnableDelete: boolean;
}

const MedicalConditionConfiguration = ({
  isEnableCreate,
  isEnableDelete,
}: MedicalConditionConfigurationProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [addMcModalState, setAddMcModalState] = useState<boolean>(false);

  const onAddMcButtonClicked = () => {
    // to ensure the dropdown list is the latest data
    queryClient.invalidateQueries(GET_DROPDOWN_MEDICAL_CONDITIONS_QUERY_KEY);
    setAddMcModalState(true);
  };

  // ------------------------------- table data logic --------------------------------------
  const { state: tableState, dispatch: tableDispatch } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'name',
    order: 'asc',
  });
  const { data, isLoading } = useGetEnabledMedicalConditions({
    onSuccess: (res) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.medicalConditions,
          count: res.medicalConditions.length,
        },
      } as TableActionTypes);
    },
  });
  // ------------------------------- table data logic --------------------------------------

  // ------------------------------- snackbar logic ----------------------------------------
  const { snackbarHandler } = useSnackbarHandler();

  // ------------------------------- snackbar logic ----------------------------------------

  // ------------------------------- Delete logic ----------------------------------------
  const [deleteModalState, setDeleteModalState] = useState<boolean>(false);
  const [selectedMedicalCondition, setSelectedMedicalCondition] = useState<MedicalCondition>({
    name: '',
    uuid: '',
  });
  const deleteMedicalCondition = usePutDisableMedicalConditions({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_ENABLED_MEDICAL_CONDITIONS_QUERY_KEY);
      onDeleteDialogClose();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>AccessArrangementConfiguration.RemoveSuccessfullyMsg', {
          name: selectedMedicalCondition.name,
        }),
      });
    },
    onError: () => {
      onDeleteDialogClose();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('admin->>AccessArrangementConfiguration.UnableToRemoveMsg', {
          name: selectedMedicalCondition.name,
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    },
  });
  const onDeleteDialogOpen = () => {
    setDeleteModalState(true);
  };
  const onDeleteDialogClose = () => {
    setDeleteModalState(false);
    deleteMedicalCondition.reset();
  };
  const onDeleteDialogConfirm = () => {
    deleteMedicalCondition.mutate(selectedMedicalCondition.uuid);
  };
  // ------------------------------- Delete logic ----------------------------------------

  return (
    <ContentTemplate type="full">
      <Box className={classes.headerContainer}>
        <HeaderWithPill
          title={t('admin->>AccessArrangementConfiguration.SelectedMedicalConditions')}
          pillValue={tableState.count.toString()}
        />
        {isEnableCreate && (
          <Button
            id="button-addMedicalCondition"
            variant="text"
            color="primary"
            startIcon={<Icon name={IconName.add} size="S" color={theme.color.icon.iconSelected} />}
            onClick={onAddMcButtonClicked}
            className={classes.addButton}
          >
            {t('admin->>AccessArrangementConfiguration.AddMedicalCondition')}
          </Button>
        )}
      </Box>
      <Typography className={classes.instruction}>
        {t('admin->>AccessArrangementConfiguration.MedicalConditionInstruction')}{' '}
      </Typography>
      <MUIDataTable
        isLoading={isLoading}
        columns={getTableColumns(
          t,
          classes,
          tableState,
          onDeleteDialogOpen,
          setSelectedMedicalCondition,
          isEnableDelete
        )}
        data={tableState.data}
        dispatch={tableDispatch}
        state={tableState}
        options={{
          sort: true,
          serverSide: false,
          sortOrder: {
            name: tableState.sortColumn,
            direction: tableState.order,
          },
        }}
      />

      {data?.dateTime != null && (
        <div className={classes.updatedBy}>
          <IconWithLabel
            iconName={IconName.info}
            iconSize="M"
            iconColor={theme.color.icon.iconSelected}
            label={t('admin->>AccessArrangementConfiguration.DataUpdatedByMessage', {
              updatedBy: data.name,
              updatedAt: stringToLongDateAndShortTime(data.dateTime),
            })}
            typographyProps={{
              variant: 'subtitle2',
              color: 'textSecondary',
            }}
          />
        </div>
      )}

      <AddMedicalConditionModal modalState={addMcModalState} setModalState={setAddMcModalState} />

      <DeleteConfirmationModal
        apiStatus={deleteMedicalCondition.status}
        dialogContentMsg={
          <HtmlTrans
            i18nKey="admin->>AccessArrangementConfiguration.RemoveConfirmationMsg"
            values={{
              name: selectedMedicalCondition.name,
            }}
          />
        }
        dialogTitle={t('admin->>AccessArrangementConfiguration.RemoveMedicalCondition')}
        modalState={deleteModalState}
        onDialogClose={onDeleteDialogClose}
        onDialogConfirm={onDeleteDialogConfirm}
      />
    </ContentTemplate>
  );
};

export default MedicalConditionConfiguration;
